import { createFileRoute, useParams, notFound, redirect, Outlet, useLocation } from '@tanstack/react-router'
import { useEffect, useState, useRef } from 'react'
import { Sidebar } from '../../components/chat/Sidebar'
import { ChatArea } from '../../components/chat/ChatArea'
import { Header } from '../../components/chat/Header'
import { useConversationStore } from '../../stores/conversationStore'
import { useMessageStore } from '../../stores/messageStore'
import { conversationAPI } from '../../services/api'
import { realtimeStreamManager } from '../../services/realtimeStreamManager'
import { cn } from '@/lib/utils'

type ViewMode = 'split' | 'chat' | 'preview'

interface ChatLayoutSearchParams {
  slide?: string
  tab?: string
}

export const Route = createFileRoute('/chat/$conversationId')({
  validateSearch: (search: Record<string, unknown>): ChatLayoutSearchParams => {
    return {
      slide: search.slide as string | undefined,
      tab: search.tab as string | undefined,
    }
  },
  component: ConversationLayout,
  loader: async ({ params, location }) => {
    // Validate that conversationId exists and is valid
    if (!params.conversationId) {
      throw notFound()
    }

    // Check if already on a child route to avoid redirect loops
    const isOnChildRoute = location.pathname.includes('/gallery') ||
      location.pathname.includes('/slide-workspace') ||
      location.pathname.includes('/slide/') ||
      location.pathname.includes('/video-workspace')

    try {
      // Try to fetch the conversation to validate it exists
      const response = await conversationAPI.getById(params.conversationId)
      const conversation = response.conversation

      // Auto-redirect to appropriate workspace if accessing base conversation route
      if (!isOnChildRoute) {
        if (conversation.collectionType === 'SLIDE') {
          throw redirect({
            to: '/chat/$conversationId/slide-workspace',
            params: { conversationId: params.conversationId },
            search: { tab: undefined }
          })
        } else {
          // video-conversations or style-conversations
          const isStyle = conversation.collectionType === 'STYLE'
          if (isStyle) {
            throw redirect({
              to: '/chat/$conversationId/gallery',
              params: { conversationId: params.conversationId },
              search: { slide: undefined, tab: undefined }
            })
          } else {
            throw redirect({
              to: '/chat/$conversationId/video-workspace',
              params: { conversationId: params.conversationId },
            })
          }
        }
      }

      return {
        conversation: response.conversation
      }
    } catch (error) {
      // If conversation doesn't exist, throw notFound
      // Note: redirect throws are caught here, so we need to re-throw them
      if (error && typeof error === 'object' && 'isRedirect' in error) {
        throw error
      }
      throw notFound()
    }
  },
})

function ConversationLayout() {
  const { conversationId } = useParams({ from: '/chat/$conversationId' })
  const { setActiveConversation } = useConversationStore()
  const location = useLocation()

  // Check if we're on a studio workspace route - they have their own full layout
  const isStudioWorkspace = location.pathname.includes('/video-workspace') ||
                            location.pathname.includes('/slide-workspace')

  const [sidebarVisible, setSidebarVisible] = useState(false)
  const [chatSidebarWidth, setChatSidebarWidth] = useState(() => {
    // Get saved width from localStorage or use default
    const saved = localStorage.getItem('chatSidebarWidth')
    return saved ? parseInt(saved, 10) : 350 // Increased default width for better readability
  })
  const [isResizing, setIsResizing] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    // Get saved view mode from localStorage or use default to split
    const saved = localStorage.getItem('chatViewMode')
    return (saved as ViewMode) || 'split'
  })

  const sidebarRef = useRef<HTMLDivElement>(null)
  const resizeRef = useRef<HTMLDivElement>(null)

  // Set active conversation when route loads - do this immediately
  useEffect(() => {
    // Only set active conversation if we're still on this conversation's route
    // This prevents race conditions when navigating away (e.g., clicking logo to go home)
    const isStillOnThisConversation = location.pathname.includes(`/chat/${conversationId}`);

    if (conversationId && isStillOnThisConversation) {
      // Always set the active conversation when the route loads, even if it's already set
      // This ensures synchronization with pending message sending
      console.log('🎯 Route: Setting active conversation:', conversationId);
      setActiveConversation(conversationId);
    }
  }, [conversationId, setActiveConversation, location.pathname])

  // Check lock status when conversation loads and restore streaming state if needed
  useEffect(() => {
    if (!conversationId) return;

    const checkLockAndRestoreStream = async () => {
      try {
        const lockStatus = await conversationAPI.getLockStatus(conversationId);
        console.log('🔒 Route: Lock status check:', lockStatus);

        if (lockStatus.locked && lockStatus.messageId) {
          const messageStore = useMessageStore.getState();

          // Set streaming message to block input
          console.log('🔒 Route: Conversation is locked, setting streaming state:', lockStatus.messageId);
          messageStore.setStreamingMessage(lockStatus.messageId);

          // Check if we already have an active stream for this message
          if (!realtimeStreamManager.hasActiveStream(lockStatus.messageId)) {
            // Reconnect to SSE stream to continue receiving updates
            console.log('📡 Route: Reconnecting to SSE stream for message:', lockStatus.messageId);
            await realtimeStreamManager.startStream(lockStatus.messageId, conversationId, 0);
          }
        }
      } catch (error) {
        // Lock status check failed - this is non-fatal, user can still use the chat
        console.warn('⚠️ Route: Failed to check lock status:', error);
      }
    };

    checkLockAndRestoreStream();
  }, [conversationId])

  // Resize functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)

    const startX = e.clientX
    const startWidth = chatSidebarWidth

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.min(Math.max(startWidth + (e.clientX - startX), 280), 800)
      setChatSidebarWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      // Save to localStorage
      localStorage.setItem('chatSidebarWidth', chatSidebarWidth.toString())
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Save width to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('chatSidebarWidth', chatSidebarWidth.toString())
  }, [chatSidebarWidth])

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('chatViewMode', viewMode)
  }, [viewMode])

  // Toggle sidebar function
  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible)
  }

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarVisible && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setSidebarVisible(false)
      }
    }

    if (sidebarVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [sidebarVisible])

  // Auto-switch to preview mode when edit mode is activated
  useEffect(() => {
    const handleEditModeChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.isEditMode === true && viewMode !== 'preview') {
        console.log('🎨 Auto-switching to preview-only mode for editing');
        setViewMode('preview');
      }
    };

    window.addEventListener('slideEditModeChanged', handleEditModeChange);
    return () => {
      window.removeEventListener('slideEditModeChanged', handleEditModeChange);
    };
  }, [viewMode, setViewMode]);

  // Studio workspaces (video/slide) have their own full layout - render directly without parent chrome
  if (isStudioWorkspace) {
    return (
      <div className="h-full w-full overflow-hidden">
        <Outlet />
      </div>
    )
  }

  return (
    <div className={cn(
      "flex flex-col h-full bg-background relative overflow-hidden transition-colors duration-300",
      isResizing && "select-none cursor-col-resize"
    )}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] pointer-events-none z-0 bg-primary/20" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] pointer-events-none z-0 bg-primary/20" />

      {/* Chat Sidebar - Click to toggle */}
      <div
        ref={sidebarRef}
        className={cn(
          "absolute left-0 top-0 h-full w-80 md:w-80 sm:w-72 bg-background/80 backdrop-blur-xl border-r border-white/10 transition-transform duration-300 ease-out z-[60] shadow-2xl",
          sidebarVisible ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-white/5">
            <h3 className="text-sm font-semibold text-primary">
              Conversations
            </h3>
          </div>
          <div className="flex-1 overflow-hidden">
            <Sidebar onChatSelect={() => setSidebarVisible(false)} />
          </div>
        </div>
      </div>

      {/* UNIFIED HEADER - Spans full width above both panels */}
      <div className="relative z-10">
        <Header
          onToggleSidebar={toggleSidebar}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      </div>

      {/* Main Content Area - Seamless Layout */}
      <div className="flex-1 flex min-w-0 w-full overflow-hidden relative z-0 p-1 gap-1 -mt-1">
        {/* Chat Area - Floating Card */}
        <div
          className={cn(
            "flex flex-col h-full relative transition-all duration-500 ease-in-out rounded-2xl overflow-hidden",
            viewMode === 'preview' ? 'w-0 opacity-0 overflow-hidden p-0' : 'floating-card',
            viewMode === 'chat' ? 'flex-1' : 'flex-shrink-0'
          )}
          style={{
            width: viewMode === 'chat' ? '100%' : viewMode === 'preview' ? '0' : `${chatSidebarWidth}px`,
            minWidth: viewMode === 'preview' ? '0' : viewMode === 'chat' ? '100%' : '300px',
            maxWidth: viewMode === 'chat' ? '100%' : '800px'
          }}
        >
          <div className="flex-1 overflow-hidden">
            <ChatArea />
          </div>

          {/* Resize Handle - Subtle & Modern */}
          {viewMode === 'split' && (
            <div
              ref={resizeRef}
              className={cn(
                "absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-primary/20 transition-all duration-300 z-50 group",
                isResizing && "bg-primary/40 w-1.5"
              )}
              onMouseDown={handleMouseDown}
              title="Drag to resize chat area"
            >
              <div className="absolute top-1/2 right-0.5 transform -translate-y-1/2 w-1 h-8 bg-border/50 rounded-full group-hover:bg-primary/50 transition-colors" />
            </div>
          )}
        </div>

        {/* Right Panel - Preview Area */}
        <div className={cn(
          "flex-1 flex flex-col min-w-0 overflow-hidden relative transition-all duration-500 ease-in-out rounded-2xl",
          viewMode === 'chat' ? 'w-0 opacity-0 p-0' : 'floating-card'
        )}>
          {/* Child route content will be rendered here */}
          <div className="flex-1 overflow-hidden flex flex-col bg-muted/10">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  )
}